import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Banner, Modal } from "@rentiohq/web-shared/dist/components";
import { EModalType } from "@rentiohq/web-shared/dist/components/Modal/Modal.types";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import React from "react";
import * as S from "./VisitBooking.styled";
import { IVisitWithName } from "./VisitBooking.types";
import { getCandidateName, getMomentLabel } from "./VisitBooking.utils";

interface IProps {
  application: IApplication;
  visit: IVisitWithName;
  /**
   * Whether the property has no bookable moment left. The server only tells the
   * invited candidates about a freed moment on the edge out of fully booked, so
   * promising that mail on a property with room to spare would be untrue.
   */
  isPropertyFullyBooked: boolean;
  onClose: () => void;
  onCancelled: () => void;
}

/**
 * Cancelling is confirmed because it does three things the broker cannot see
 * from here: it reaches the candidate through the calendar invite, it frees the
 * moment, and it moves the candidacy back a step.
 */
export const CancelVisitModal = (props: IProps) => {
  const { application, visit, isPropertyFullyBooked, onClose, onCancelled } =
    props;

  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();

  const candidateName = getCandidateName(application);

  const handleConfirm = async () => {
    setIsSaving(true);
    setError(undefined);

    try {
      await api.delete(`/cronofy/visits/${visit.id}`, { isV2: true });
    } catch {
      setError(getLocalizedText("visit_booking.cancel.error"));
      setIsSaving(false);

      return;
    }

    // Outside the try on purpose: the visit is already cancelled, so a throw
    // from the toast or the refetch must not read as a cancellation that failed
    // and invite a second attempt.
    setIsSaving(false);
    toast({
      heading: getLocalizedText("visit_booking.cancel.done", {
        name: candidateName,
      }),
      variation: "success",
    });
    onCancelled();
    onClose();
  };

  return (
    <Modal
      width="small"
      // The house way of marking a dialog whose confirm button does something
      // that cannot be taken back: it tints the header rather than leaving the
      // consequences to be read off plain body text.
      type={EModalType.Warning}
      heading={getLocalizedText("visit_booking.cancel.heading")}
      hasDismiss={true}
      onClose={onClose}
      actions={[
        {
          content: getLocalizedText("visit_booking.cancel.keep"),
          onClick: onClose,
          appearance: "outline",
        },
        {
          content: getLocalizedText("visit_booking.cancel.confirm"),
          onClick: handleConfirm,
          appearance: "primary",
          isDisabled: isSaving,
        },
      ]}
    >
      {error && (
        <Banner variation="error" hasDismiss={false}>
          {error}
        </Banner>
      )}

      <S.CancelIntro>
        {getLocalizedText("visit_booking.cancel.intro", {
          name: candidateName,
          moment: getMomentLabel(visit.start),
        })}
      </S.CancelIntro>

      <S.ConsequenceList>
        <S.Consequence>
          {getLocalizedText("visit_booking.cancel.mail", {
            name: candidateName,
          })}
        </S.Consequence>
        <S.Consequence>
          {getLocalizedText(
            isPropertyFullyBooked
              ? "visit_booking.cancel.slot"
              : "visit_booking.cancel.slot_simple",
          )}
        </S.Consequence>
        <S.Consequence>
          {getLocalizedText("visit_booking.cancel.step", {
            name: candidateName,
          })}
        </S.Consequence>
      </S.ConsequenceList>
    </Modal>
  );
};
